import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { useDebounce } from '../../hooks/Debounce'
import { HostData } from '../Customer/Data'
import { CustomerIntelligenceTable } from './CustomerIntelligenceTable'
import { useCustomerSummaries } from './CustomerSummaries'
import { DemoOptionsPanel, useDemoOptions } from './DemoOptionsPanel' /* DEMO ONLY */
import demoStyles from './DemoOptionsPanel.module.css' /* DEMO ONLY */
import {
  OfficePrinterIcon,
  FleetPerformanceSectionIcon,
  FeatureUtilizationSectionIcon,
  CurrencyIcon,
} from '../Icons/Icons' /* DEMO ONLY */
import styles from './IntelligenceHub.module.css'
import { IntelligenceHubScene } from './IntelligenceHubScene'

interface Props {
  teamID: string
  hosts: HostData[]
}

export function filterAndSortHosts(hosts: HostData[], term: string): HostData[] {
  const t = term.trim().toLowerCase()
  return (t ? hosts.filter(h => h.name.toLowerCase().includes(t)) : hosts)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
}

export const IntelligenceHub: FunctionComponent<Props> = props => {
  const hosts = useMemo(() => props.hosts || [], [props.hosts])
  const [searchFilter, setSearchFilter] = useState('')
  const debouncedFilter = useDebounce(searchFilter, 500)

  const { summaries, status: summariesStatus, reload: reloadSummaries } = useCustomerSummaries(props.teamID)
  const { options } = useDemoOptions() /* DEMO ONLY */

  // Save valid first customer ID to localStorage for fallback navigation in demo panel
  useEffect(() => {
    const existing = localStorage.getItem('last_visited_customer_id')
    if ((!existing || existing === 'demo-baseline') && hosts.length > 0) {
      localStorage.setItem('last_visited_customer_id', hosts[0].id)
    }
  }, [hosts])

  /* Banner links go to the last customer visited, or the first alphabetically */
  const sceneCustomerID = useMemo(() => {
    let lastVisited: string | null = null
    try {
      lastVisited = localStorage.getItem('last_visited_customer_id')
    } catch {
      lastVisited = null
    }
    if (lastVisited && hosts.some(h => h.id === lastVisited)) return lastVisited
    return filterAndSortHosts(hosts, '')[0]?.id
  }, [hosts])

  /* DEMO ONLY: Circuit's brief dropdowns list the team's customers, alphabetically */
  const pickerCustomers = useMemo(() => filterAndSortHosts(hosts, '').map(h => ({ id: h.id, name: h.name })), [hosts])

  const term = debouncedFilter.trim().toLowerCase()
  const filteredHosts = filterAndSortHosts(hosts, debouncedFilter)

  /* DEMO ONLY: Compute Totals for Hub Metric Surfaces */
  const summaryList = Array.from(summaries.values())
  const totalDevices = summaryList.reduce((acc, s) => acc + (s.deviceCount || 0), 0)
  const totalVolume = summaryList.reduce((acc, s) => acc + (s.printVolume || 0), 0)
  const totalFleetValue = summaryList.reduce((acc, s) => acc + (s.estimatedFleetValue || 0), 0)
  const highRiskCount = summaryList.filter(s => Boolean(s.risk && ['High', 'Very High', 'Extreme'].includes(s.risk)))
    .length

  return (
    <div className='tab' id='tab-intelligence-hub'>
      <header>
        <div className='auto'>
          <h2>Intelligence Hub</h2>
        </div>
      </header>

      {/* DEMO ONLY: Intelligence Hub banner (toggle in demo options) */}
      {options.showHubPitch && (
        <IntelligenceHubScene
          customerID={sceneCustomerID}
          customers={pickerCustomers}
          variant={options.hubBannerStyle}
          stats={
            summariesStatus === 'loaded'
              ? {
                  customers: hosts.length,
                  devices: totalDevices,
                  printVolume: totalVolume,
                  fleetValue: totalFleetValue,
                  riskFlags: highRiskCount,
                }
              : undefined
          }
        />
      )}

      <div className='auto rowview'>
        {/* DEMO ONLY: Aggregated Metric Surfaces (Below Pitch) */}
        {options.showHubMetrics && summariesStatus === 'loaded' && (
          <div className={demoStyles.demoMetricsGrid}>
            <div className={demoStyles.demoMetricTile}>
              <i className={`icon thirtytwo flex-noshrink ${demoStyles.demoMetricIcon}`}>
                <OfficePrinterIcon />
              </i>
              <div className={demoStyles.demoMetricContent}>
                <div className={demoStyles.demoMetricValue}>{totalDevices.toLocaleString()}</div>
                <h5 className={demoStyles.demoMetricLabel}>Devices</h5>
              </div>
            </div>

            <div className={demoStyles.demoMetricTile}>
              <i className={`icon thirtytwo flex-noshrink ${demoStyles.demoMetricIcon}`}>
                <FleetPerformanceSectionIcon />
              </i>
              <div className={demoStyles.demoMetricContent}>
                <div className={demoStyles.demoMetricValue}>{totalVolume.toLocaleString()}</div>
                <h5 className={demoStyles.demoMetricLabel}>Print volume</h5>
              </div>
            </div>

            <div className={demoStyles.demoMetricTile}>
              <i className={`icon thirtytwo flex-noshrink ${demoStyles.demoMetricIcon}`}>
                <FeatureUtilizationSectionIcon />
              </i>
              <div className={demoStyles.demoMetricContent}>
                <div className={highRiskCount > 0 ? demoStyles.demoMetricValueRisk : demoStyles.demoMetricValue}>
                  {highRiskCount}
                </div>
                <h5 className={demoStyles.demoMetricLabel}>Risk flags</h5>
              </div>
            </div>

            <div className={demoStyles.demoMetricTile}>
              <i className={`icon thirtytwo flex-noshrink ${demoStyles.demoMetricIcon}`}>
                <CurrencyIcon />
              </i>
              <div className={demoStyles.demoMetricContent}>
                <div className={demoStyles.demoMetricValue}>
                  ${totalFleetValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </div>
                <h5 className={demoStyles.demoMetricLabel}>Estimated fleet value</h5>
              </div>
            </div>
          </div>
        )}

        {summariesStatus === 'error' && (
          <div className={styles.summariesError} role='status'>
            <div className={styles.summariesErrorHeader}>
              <i className={styles.summariesErrorIcon}>
                <svg fill='none' viewBox='-1 -1 24 24'>
                  <path
                    stroke='currentColor'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M12.203583333333333 2.0856091666666665c-0.11889166666666667 -0.21514166666666665 -0.2932416666666667 -0.3944875 -0.5048999999999999 -0.5193833333333333 -0.2116583333333333 -0.12490499999999999 -0.45292499999999997 -0.19077666666666665 -0.6986833333333333 -0.19077666666666665 -0.24584999999999999 0 -0.48711666666666664 0.06587166666666666 -0.6987749999999999 0.19077666666666665 -0.2116583333333333 0.12489583333333333 -0.3860083333333333 0.30424166666666663 -0.5048083333333333 0.5193833333333333L0.8588873333333333 18.5856c-0.11557516666666666 0.20936666666666665 -0.1745608333333333 0.445225 -0.17113433333333333 0.6842916666666667 0.0034265 0.23906666666666662 0.069146 0.4731833333333333 0.19067216666666664 0.6790666666666667 0.12152983333333332 0.205975 0.2946515 0.3766583333333333 0.502304 0.49527499999999997 0.20764333333333332 0.11852499999999999 0.44262166666666664 0.18094999999999997 0.6817433333333334 0.18094999999999997H19.9375c0.23906666666666662 0 0.47409999999999997 -0.062424999999999994 0.681725 -0.18094999999999997 0.207625 -0.11861666666666665 0.3807833333333333 -0.2893 0.5023333333333333 -0.49527499999999997 0.12145833333333333 -0.2058833333333333 0.1871833333333333 -0.43999999999999995 0.19066666666666665 -0.6790666666666667 0.0033916666666666665 -0.23906666666666662 -0.05564166666666666 -0.474925 -0.17114166666666666 -0.6842916666666667l-8.9375 -16.49999083333333Z'
                    strokeWidth='2'
                  ></path>
                  <path
                    stroke='currentColor'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M11 13.75V7.5625'
                    strokeWidth='2'
                  ></path>
                  <path
                    stroke='currentColor'
                    d='M11 17.1875c-0.18984166666666666 0 -0.34375 -0.1539083333333333 -0.34375 -0.34375S10.810158333333332 16.5 11 16.5'
                    strokeWidth='2'
                  ></path>
                  <path
                    stroke='currentColor'
                    d='M11 17.1875c0.18984166666666666 0 0.34375 -0.1539083333333333 0.34375 -0.34375S11.189841666666666 16.5 11 16.5'
                    strokeWidth='2'
                  ></path>
                </svg>
              </i>
              <span> Customer summaries could not be loaded.</span>
            </div>
            <div className={styles.summariesErrortMessage}>
              We couldn't retrieve customer metrics right now. Try refreshing the page, or check back in a few minutes.
            </div>
          </div>
        )}

        <CustomerIntelligenceTable
          teamID={props.teamID}
          hosts={filteredHosts}
          summaries={summaries}
          summariesStatus={summariesStatus}
          onUpdated={reloadSummaries}
          searchFilter={searchFilter}
          onSearchChange={setSearchFilter}
          searchTerm={term}
        />
      </div>

      {/* DEMO ONLY: Floating Options Widget */}
      <DemoOptionsPanel />
    </div>
  )
}
